import { z } from 'zod'
import { ApiError, type ApiClient } from '@fusapp-work/api'
import { parseResponse } from '../../shared/response'
import { collectListPages, consolePageSize, listPageSchema } from '../../shared/listPage'
const text = z.string().nullable()
const member = z.object({ objectId: text, displayName: text, principalType: text, assignedAt: text })
const roleFields = z.object({ id: z.string(), name: text, displayName: text, description: text, isEnabled: z.boolean().nullable(), permissions: z.null(), permissionsSource: z.string(), source: z.literal('EntraDirectory'), observedAt: z.string() })
export const roleSchema = roleFields.extend({ memberCount: z.number().int().nonnegative(), system: z.null(), members: z.never().optional() })
export type ConsoleRole = z.infer<typeof roleSchema>
const facts = z.object({ source: z.literal('EntraDirectory'), directoryComplete: z.literal(true), membershipSemantics: z.literal('DirectUserAndGroupAssignments'), observedAt: z.string() })
const rolesPage = listPageSchema(roleSchema, 'name:asc').extend(facts.shape)
const membersPage = listPageSchema(member, 'displayName:asc')
const detailSchema = roleSchema.extend({ members: membersPage })
// Staging before W-06 returns all members on both list and detail. A paged reply never uses this fallback.
const legacyRole = roleFields.extend({ memberCount: z.never().optional(), system: z.null().optional(), members: z.array(member) })
const legacyList = facts.extend({ items: z.array(legacyRole), page: z.never().optional(), size: z.never().optional(), totalCount: z.never().optional(), totalUnfiltered: z.never().optional(), appliedSort: z.never().optional() })
function legacyRow({ members, ...row }: z.infer<typeof legacyRole>): ConsoleRole {
  return { ...row, memberCount: members.length, system: null }
}
const listResponse = z.union([rolesPage, legacyList])
const detailResponse = z.union([detailSchema, legacyRole])
export type ConsoleRoleDetail = Omit<ConsoleRole, 'members'> & { members: { items: z.infer<typeof member>[] } }

class RoleMemberCountChanged extends ApiError {
  constructor() { super('Role member count changed during paging', 200, 'INVALID_RESPONSE') }
}

async function pagedRoleDetail(first: z.infer<typeof detailSchema>, read: (page: number) => Promise<unknown>): Promise<ConsoleRoleDetail> {
  const items = await collectListPages(first.members, async page => {
    const next = parseResponse(detailSchema, await read(page))
    if (next.id !== first.id) throw new ApiError('Role changed during paging', 200, 'INVALID_RESPONSE')
    // observedAt is the 45-second catalogue cache timestamp, not a snapshot token.
    if (next.members.totalCount !== first.members.totalCount) throw new RoleMemberCountChanged()
    return next.members
  })
  const ids = items.flatMap(item => item.objectId === null ? [] : [item.objectId])
  if (new Set(ids).size !== ids.length) throw new ApiError('Duplicate role member during paging', 200, 'INVALID_RESPONSE')
  return { ...parseResponse(roleSchema.omit({ members: true }), first), members: { items } }
}

export function createRolesApi(client: ApiClient) {
  const listPath = (page: number) => `/api/v1/console/roles?page=${page}&size=${consolePageSize}`
  return {
    async list(signal?: AbortSignal) {
      const first = parseResponse(listResponse, await client.request<unknown>(listPath(1), { signal }))
      const items = 'size' in first && first.size !== undefined
        ? await collectListPages(first, async page => parseResponse(rolesPage, await client.request<unknown>(listPath(page), { signal })))
        : parseResponse(legacyList, first).items.map(legacyRow)
      return { ...parseResponse(facts, first), items }
    },
    async detail(id: string, signal?: AbortSignal): Promise<ConsoleRoleDetail> {
      const path = (page: number) => `/api/v1/console/roles/${encodeURIComponent(id)}?page=${page}&size=${consolePageSize}`
      const read = async (page: number) => {
        const role = parseResponse(detailResponse, await client.request<unknown>(path(page), { signal }))
        if (role.id !== id && role.name !== id || !Array.isArray(role.members) && role.memberCount !== role.members.totalCount) throw new ApiError('Invalid role detail', 200, 'INVALID_RESPONSE')
        return role
      }
      const first = await read(1)
      if (Array.isArray(first.members)) {
        const legacy = parseResponse(legacyRole, first)
        return { ...legacyRow(legacy), members: { items: legacy.members } }
      }
      try {
        return await pagedRoleDetail(parseResponse(detailSchema, first), read)
      } catch (error) {
        if (!(error instanceof RoleMemberCountChanged)) throw error
        // Discard the first traversal. A second count change propagates as INVALID_RESPONSE.
        const restarted = parseResponse(detailSchema, await read(1))
        if (restarted.id !== first.id) throw new ApiError('Role changed during paging', 200, 'INVALID_RESPONSE')
        return pagedRoleDetail(restarted, read)
      }
    },
  }
}
